import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import TimePicker from './ui/time-picker';
import { Clock, Calendar as CalendarIcon, X, Edit, Trash2, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Switch } from './ui/switch';

const localizer = momentLocalizer(moment);

const ClassScheduleCalendar = ({ tasks = [], onScheduleChange }) => {
  const { getAccessToken } = useAuth();
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());  // Add date state for navigation
  const [events, setEvents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showModifyTimeModal, setShowModifyTimeModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updateAllFuture, setUpdateAllFuture] = useState(false);
  
  // Edit form state
  const [editFormData, setEditFormData] = useState({
    scheduled_time: '',
    duration_minutes: 60,
    meeting_link: ''
  });
  
  // Log tasks for debugging
  useEffect(() => {
    console.log('ClassScheduleCalendar received tasks:', tasks);
  }, [tasks]);
  
  // Form state
  const [formData, setFormData] = useState({
    task_id: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    recurrence: 'none',
    recurrence_end_date: '',
    custom_days: [],
    meeting_link: ''
  });

  // Generate consistent colors for tasks
  const getTaskColor = (taskId) => {
    const colors = [
      '#0891b2', // cyan
      '#6366f1', // indigo
      '#8b5cf6', // violet
      '#ec4899', // pink
      '#f59e0b', // amber
      '#10b981', // emerald
      '#ef4444', // red
      '#3b82f6', // blue
    ];
    // Generate consistent color based on task ID
    let hash = 0;
    for (let i = 0; i < taskId.length; i++) {
      hash = taskId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Fetch schedules
  const fetchSchedules = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const schedules = await response.json();
        
        // Convert schedules to calendar events
        const calendarEvents = schedules.map(schedule => {
          // Parse the UTC date and time
          const [year, month, day] = schedule.scheduled_date.split('-').map(Number);
          const [hours, minutes] = schedule.scheduled_time.split(':').map(Number);
          
          // Create Date object in UTC
          const startDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
          const endDate = new Date(startDate.getTime() + schedule.duration_minutes * 60000);
          
          // Find task details
          const task = tasks.find(t => t.id === schedule.task_id) || {};
          
          return {
            id: schedule.id,
            title: task.title || 'Class',
            start: startDate,
            end: endDate,
            resource: {
              ...schedule,
              taskTitle: task.title,
              taskColor: getTaskColor(schedule.task_id)
            }
          };
        });
        
        setEvents(calendarEvents);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  }, [getAccessToken, tasks]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Handle slot selection (clicking on calendar)
  const handleSelectSlot = useCallback((slotInfo) => {
    setSelectedSlot(slotInfo);
    setFormData({
      ...formData,
      scheduled_date: moment(slotInfo.start).format('YYYY-MM-DD'),
      scheduled_time: moment(slotInfo.start).format('HH:mm:ss')
    });
    setShowCreateModal(true);
  }, [formData]);

  // Handle event selection (clicking on existing event)
  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setShowEditModal(true);
  }, []);

  // Handle create schedule
  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getAccessToken();
      if (!token) {
        alert('Please log in to create schedules');
        return;
      }

      console.log('Creating schedule with data:', formData);
      console.log('Available tasks:', tasks.map(t => ({ id: t.id, title: t.title })));

      // Add seconds to time if not present (HTML time input only gives HH:mm)
      const scheduleData = {
        ...formData,
        scheduled_time: formData.scheduled_time.includes(':') && formData.scheduled_time.split(':').length === 2 
          ? `${formData.scheduled_time}:00` 
          : formData.scheduled_time
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(scheduleData)
        }
      );

      if (response.ok) {
        await fetchSchedules();
        // Notify parent component to refresh its schedules
        if (onScheduleChange) {
          onScheduleChange();
        }
        setShowCreateModal(false);
        resetForm();
        alert('Schedule created successfully!');
      } else {
        const error = await response.json();
        console.error('Schedule creation failed:', error);
        alert(`Failed to create schedule: ${error.detail || JSON.stringify(error)}`);
      }
    } catch (error) {
      console.error('Failed to create schedule:', error);
      alert(`Failed to create schedule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete schedule
  const handleDeleteSchedule = async (deleteAll = false) => {
    const confirmMessage = deleteAll 
      ? 'Are you sure you want to delete ALL occurrences of this recurring class?' 
      : 'Are you sure you want to delete this class?';
    
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      const token = getAccessToken();
      if (!token) return;

      const url = `${process.env.REACT_APP_BACKEND_URL}/api/schedules/${selectedEvent.id}${deleteAll ? '?delete_all=true' : ''}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchSchedules();
        // Notify parent component to refresh its schedules
        if (onScheduleChange) {
          onScheduleChange();
        }
        setShowEditModal(false);
        const result = await response.json();
        alert(result.message || 'Schedule deleted successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to delete schedule: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      alert('Failed to delete schedule');
    } finally {
      setLoading(false);
    }
  };

  // Open modify time modal
  const handleOpenModifyTime = () => {
    if (!selectedEvent) return;
    
    // Pre-fill the edit form with current values
    const currentTime = moment(selectedEvent.start).format('HH:mm');
    setEditFormData({
      scheduled_time: currentTime,
      duration_minutes: selectedEvent.resource.duration_minutes || 60,
      meeting_link: selectedEvent.resource.meeting_link || ''
    });
    setUpdateAllFuture(false);
    setShowEditModal(false);
    setShowModifyTimeModal(true);
  };

  // Handle update schedule time
  const handleUpdateScheduleTime = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getAccessToken();
      if (!token) {
        alert('Please log in to update schedules');
        return;
      }

      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Prepare update data with timezone for proper conversion
      const updateData = {
        scheduled_time: editFormData.scheduled_time.includes(':') && editFormData.scheduled_time.split(':').length === 2 
          ? `${editFormData.scheduled_time}:00` 
          : editFormData.scheduled_time,
        duration_minutes: editFormData.duration_minutes,
        meeting_link: editFormData.meeting_link,
        timezone: userTimezone,  // Send timezone for proper UTC conversion
        scheduled_date: moment(selectedEvent.start).format('YYYY-MM-DD')  // Send the original date for timezone conversion
      };

      const url = `${process.env.REACT_APP_BACKEND_URL}/api/schedules/${selectedEvent.id}${updateAllFuture ? '?update_all_future=true' : ''}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await fetchSchedules();
        // Notify parent component to refresh its schedules
        if (onScheduleChange) {
          onScheduleChange();
        }
        setShowModifyTimeModal(false);
        const successMsg = updateAllFuture 
          ? 'All future class schedules updated successfully!' 
          : 'Class schedule updated successfully!';
        alert(successMsg);
      } else {
        const error = await response.json();
        console.error('Schedule update failed:', error);
        alert(`Failed to update schedule: ${error.detail || JSON.stringify(error)}`);
      }
    } catch (error) {
      console.error('Failed to update schedule:', error);
      alert(`Failed to update schedule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      task_id: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 60,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      recurrence: 'none',
      recurrence_end_date: '',
      custom_days: [],
      meeting_link: ''
    });
  };

  const customDayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 text-cyan-600" />
          Class Schedule
        </h2>
        <div className="flex gap-2">
          <Button
            onClick={() => setView('month')}
            variant={view === 'month' ? 'default' : 'outline'}
            size="sm"
          >
            Month
          </Button>
          <Button
            onClick={() => setView('week')}
            variant={view === 'week' ? 'default' : 'outline'}
            size="sm"
          >
            Week
          </Button>
          <Button
            onClick={() => setView('day')}
            variant={view === 'day' ? 'default' : 'outline'}
            size="sm"
          >
            Day
          </Button>
        </div>
      </div>

      <div style={{ height: '600px' }}>
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          date={date}
          onView={setView}
          onNavigate={(newDate) => setDate(newDate)}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          selectable
          style={{ height: '100%' }}
          eventPropGetter={(event) => ({
            style: {
              backgroundColor: event.resource?.taskColor || '#0891b2',
              borderRadius: '6px',
              opacity: 0.9,
              color: 'white',
              border: '0px',
              display: 'block'
            }
          })}
        />
      </div>

      {/* Create Schedule Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Class Schedule</DialogTitle>
            <DialogDescription>
              Schedule a new class session
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateSchedule} className="space-y-4">
            {tasks.length === 0 ? (
              <div className="text-center py-4 text-amber-600 bg-amber-50 rounded-lg">
                <p className="text-sm">No tasks available. Please contact admin to assign tasks before creating schedules.</p>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="task">Select Task</Label>
                  <Select
                    value={formData.task_id}
                    onValueChange={(value) => setFormData({ ...formData, task_id: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a task" />
                    </SelectTrigger>
                    <SelectContent>
                      {tasks.map((task) => (
                        <SelectItem key={task.id} value={task.id}>
                          {task.title} - {task.subject}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="time">Time</Label>
                <TimePicker
                  value={formData.scheduled_time}
                  onChange={(time) => setFormData({ ...formData, scheduled_time: time })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                min="15"
                step="15"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                required
              />
            </div>

            <div>
              <Label htmlFor="meet_link">Google Meet Link (Optional)</Label>
              <Input
                id="meet_link"
                type="url"
                placeholder="https://meet.google.com/..."
                value={formData.meeting_link}
                onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="recurrence">Recurrence</Label>
              <Select
                value={formData.recurrence}
                onValueChange={(value) => setFormData({ ...formData, recurrence: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Recurrence</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="custom">Custom Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.recurrence !== 'none' && (
              <div>
                <Label htmlFor="end_date">Recurrence End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.recurrence_end_date}
                  onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                  required
                />
              </div>
            )}

            {formData.recurrence === 'custom' && (
              <div>
                <Label>Select Days</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {customDayNames.map((day, index) => (
                    <label key={index} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.custom_days.includes(index)}
                        onChange={(e) => {
                          const days = e.target.checked
                            ? [...formData.custom_days, index]
                            : formData.custom_days.filter(d => d !== index);
                          setFormData({ ...formData, custom_days: days.sort() });
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{day.slice(0, 3)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || tasks.length === 0}>
                {loading ? 'Creating...' : 'Create Schedule'}
              </Button>
            </div>
            </>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit/View Schedule Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Class Details</DialogTitle>
          </DialogHeader>

          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <Label>Task Name</Label>
                <p className="text-sm font-medium mt-1">{selectedEvent.resource.taskTitle || 'N/A'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <p className="text-sm mt-1">{moment(selectedEvent.start).format('MMMM DD, YYYY')}</p>
                </div>
                <div>
                  <Label>Time</Label>
                  <p className="text-sm mt-1">{moment(selectedEvent.start).format('hh:mm A')}</p>
                </div>
              </div>
              <div>
                <Label>Duration</Label>
                <p className="text-sm mt-1">{selectedEvent.resource.duration_minutes} minutes</p>
              </div>
              {selectedEvent.resource.meeting_link && (
                <div>
                  <Label>Google Meet Link</Label>
                  <a 
                    href={selectedEvent.resource.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-600 hover:text-cyan-700 underline mt-1 block"
                  >
                    Join Meeting
                  </a>
                </div>
              )}

              <div className="flex justify-between gap-2 pt-4 border-t">
                {/* Edit Time Button */}
                <Button
                  variant="outline"
                  onClick={handleOpenModifyTime}
                  className="flex items-center gap-2"
                  data-testid="edit-schedule-time-btn"
                >
                  <Edit className="w-4 h-4" />
                  Edit Time
                </Button>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(false)}
                  >
                    Close
                  </Button>
                  {selectedEvent.resource.recurrence_group_id ? (
                    <>
                      <Button
                        variant="destructive"
                        onClick={() => handleDeleteSchedule(false)}
                        disabled={loading}
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        This
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleDeleteSchedule(true)}
                        disabled={loading}
                        className="bg-red-700 hover:bg-red-800"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        All
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="destructive"
                      onClick={() => handleDeleteSchedule(false)}
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modify Time Modal */}
      <Dialog open={showModifyTimeModal} onOpenChange={setShowModifyTimeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-600" />
              Modify Class Time
            </DialogTitle>
            <DialogDescription>
              Update the time and duration for this class
            </DialogDescription>
          </DialogHeader>

          {selectedEvent && (
            <form onSubmit={handleUpdateScheduleTime} className="space-y-4">
              {/* Current Date Info */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <Label className="text-gray-500 dark:text-gray-400">Selected Class</Label>
                <p className="text-sm font-medium mt-1">
                  {selectedEvent.resource.taskTitle || 'Class'} - {moment(selectedEvent.start).format('MMMM DD, YYYY')}
                </p>
              </div>

              {/* Time Picker */}
              <div>
                <Label htmlFor="edit_time">New Time</Label>
                <TimePicker
                  value={editFormData.scheduled_time}
                  onChange={(time) => setEditFormData({ ...editFormData, scheduled_time: time })}
                />
              </div>

              {/* Duration */}
              <div>
                <Label htmlFor="edit_duration">Duration (minutes)</Label>
                <Input
                  id="edit_duration"
                  type="number"
                  min="15"
                  step="15"
                  value={editFormData.duration_minutes}
                  onChange={(e) => setEditFormData({ ...editFormData, duration_minutes: parseInt(e.target.value) })}
                  required
                />
              </div>

              {/* Meeting Link */}
              <div>
                <Label htmlFor="edit_meet_link">Google Meet Link (Optional)</Label>
                <Input
                  id="edit_meet_link"
                  type="url"
                  placeholder="https://meet.google.com/..."
                  value={editFormData.meeting_link}
                  onChange={(e) => setEditFormData({ ...editFormData, meeting_link: e.target.value })}
                />
              </div>

              {/* Update All Future Toggle - Only show for recurring schedules */}
              {selectedEvent.resource.recurrence_group_id && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-amber-800 dark:text-amber-200 font-medium">
                        Apply to all future classes
                      </Label>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        This will update all upcoming classes in this recurring series
                      </p>
                    </div>
                    <Switch
                      checked={updateAllFuture}
                      onCheckedChange={setUpdateAllFuture}
                      data-testid="update-all-future-toggle"
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModifyTimeModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="save-schedule-changes-btn"
                >
                  {loading ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {updateAllFuture ? 'Update All Future' : 'Save Changes'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassScheduleCalendar;
